import React, { useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import CustomButton from '../../../components/CustomButton';
import { HubImportResponse } from '../../../types/HubImport';
import {
  importHubs,
  previewHubImport,
} from '../../../services/hubs';

type Props = {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
};

const ImportDialog: React.FC<Props> = ({
  open,
  onClose,
  onImported,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<HubImportResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);

  const resetState = () => {
    setFile(null);
    setPreview(null);
    setPreviewLoading(false);
    setImportLoading(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const runPreview = async (selectedFile: File) => {
    setPreviewLoading(true);

    try {
      const response = await previewHubImport({
        file: selectedFile,
      });

      setPreview(response);
    } catch {
      enqueueSnackbar('Import failed! Please check column names in file', {
        variant: 'error',
      });
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setFile(selectedFile);
    setPreview(null);
    await runPreview(selectedFile);
  };

  const handleChooseFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleImport = async () => {
    if (!file || !preview) {
      return;
    }

    setImportLoading(true);

    try {
      const response = await importHubs({ file });

      const { importedRows, skippedDuplicates, skippedInvalid } = response.summary;

      const messages = [
        `${importedRows} device(s) imported`,
        skippedDuplicates > 0 && `${skippedDuplicates} duplicate(s) skipped`,
        skippedInvalid > 0 && `${skippedInvalid} invalid row(s) skipped`,
      ].filter(Boolean);

      enqueueSnackbar(
        `${messages.join('. ')}.`,
        { variant: response.summary.skippedDuplicates || response.summary.skippedInvalid ? 'warning' : 'success' },
      );
      onImported();
      handleClose();
    } catch (error) {
      const requestError = error as any;
      enqueueSnackbar(requestError?.message || 'Import failed', {
        variant: 'error',
      });
    } finally {
      setImportLoading(false);
    }
  };

  const canImport = useMemo(
    () => !!file && !!preview && !previewLoading && !importLoading,
    [file, preview, previewLoading, importLoading],
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle disableTypography>
        <Typography variant="h6">Import Devices</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <CustomButton
              variant="orange"
              onClick={handleChooseFileClick}
              loading={previewLoading}
              disabled={importLoading}
            >
              Choose file
            </CustomButton>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2">
              Selected file:
              {' '}
              {file?.name || 'No file selected'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Maximum file size: 1 MB. Supported format: .xlsx
            </Typography>
          </Grid>
          {preview && (
            <>
              <Grid item xs={12}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Serial Number</TableCell>
                      <TableCell>Device Type</TableCell>
                      <TableCell>MAC</TableCell>
                      <TableCell>IMEI</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.previewRows.map(row => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.serialNumber}</TableCell>
                        <TableCell>{row.deviceType}</TableCell>
                        <TableCell>{row.mac}</TableCell>
                        <TableCell>{row.imei}</TableCell>
                        <TableCell>{row.status}</TableCell>
                        <TableCell>{row.error || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              <Grid item xs={12} sm={3}>
                <Typography variant="body2">
                  Total rows:
                  {' '}
                  {preview.summary.totalRows}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2">
                  Valid rows:
                  {' '}
                  {preview.summary.validRows}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2">
                  Duplicate rows:
                  {' '}
                  {preview.summary.skippedDuplicates}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="body2">
                  Invalid rows:
                  {' '}
                  {preview.summary.skippedInvalid}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <CustomButton variant="white" onClick={handleClose}>
          Cancel
        </CustomButton>
        <CustomButton
          variant="orange"
          onClick={handleImport}
          loading={importLoading}
          disabled={!canImport}
        >
          Import
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ImportDialog);
